"use client";

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Drawer as DrawerPrimitive } from "vaul";
import { X } from "lucide-react";

import { AssetImage } from "@/components/optilase/asset-image";
import { CarouselDots } from "@/components/optilase/carousel-dots";
import { activeSlide, scrollToAdjacent } from "@/components/optilase/carousel-page";
import { Chevron } from "@/components/optilase/icons";
import YouTubePlayer from "@/components/video/youtube-player";
import { ButtonLink } from "@/components/ui/button";
import { useHorizontalOverflow } from "@/lib/use-scrollable";
import { ANALYTICS_EVENTS, track } from "@/lib/analytics";
import { useConsent } from "@/lib/consent-context";
import { useRovingFocus } from "@/lib/use-roving-focus";
import { youtubeShortThumbnail } from "@/lib/youtube";
import { accentOn, type Surface } from "@/components/optilase/surface";
import { cn } from "@/lib/utils";

export type TestimonialVideo = {
  id?: string;
  title?: string;
  poster?: string;
};

export type TestimonialsViewProps = {
  heading?: string;
  videos: TestimonialVideo[];
  ctaLabel?: string;
  ctaHref?: string;
  surface?: Surface | null;
};

const DESKTOP_QUERY = "(min-width: 768px)";
const PREVIEW_LOOP_END_SECONDS = 6;
const POSTER_FADE_DELAY_MS = 1500;
function subscribeDesktop(onChange: () => void) {
  const mq = window.matchMedia(DESKTOP_QUERY);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

function useIsDesktop() {
  return useSyncExternalStore(
    subscribeDesktop,
    () => window.matchMedia(DESKTOP_QUERY).matches,
    () => false,
  );
}

function StoryCard({
  video,
  onOpen,
  isPreviewing,
  onPreviewIntent,
  buttonRef,
  onKeyDown,
  isActive,
}: {
  video: TestimonialVideo;
  onOpen: (id: string) => void;
  isPreviewing: boolean;
  onPreviewIntent: () => void;
  buttonRef?: (el: HTMLButtonElement | null) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  isActive: boolean;
}) {
  const { id, poster, title } = video;
  const { consent } = useConsent();
  const showPreview = Boolean(id) && isPreviewing && consent.marketing;
  const [posterHidden, setPosterHidden] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el || near) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setNear(true);
      },
      { rootMargin: "100% 100%" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [near]);

  useEffect(() => {
    let active = true;
    queueMicrotask(() => {
      if (active) setPosterHidden(false);
    });
    const t = showPreview
      ? setTimeout(() => {
          if (active) setPosterHidden(true);
        }, POSTER_FADE_DELAY_MS)
      : null;
    return () => {
      active = false;
      if (t) clearTimeout(t);
    };
  }, [showPreview]);

  const previewParams = new URLSearchParams({
    autoplay: "1",
    mute: "1",
    controls: "0",
    modestbranding: "1",
    rel: "0",
    iv_load_policy: "3",
    playsinline: "1",
    disablekb: "1",
    fs: "0",
    cc_load_policy: "0",
    loop: "1",
    playlist: id ?? "",
    start: "0",
    end: String(PREVIEW_LOOP_END_SECONDS),
  }).toString();

  const fit = poster
    ? "absolute inset-0 size-full object-cover"
    : "absolute top-[-20%] left-0 h-[140%] w-full object-cover";

  return (
    <div
      ref={cardRef}
      className="bg-navy-03 relative aspect-9/16 w-[234px] shrink-0 overflow-hidden rounded-2xl lg:w-68"
      onMouseEnter={onPreviewIntent}
      onFocus={onPreviewIntent}
    >
      {showPreview && id && (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?${previewParams}`}
          title={title || "Patient story preview"}
          tabIndex={-1}
          aria-hidden
          loading="lazy"
          className="pointer-events-none absolute top-[-20%] left-0 h-[140%] w-full"
          allow="autoplay; encrypted-media; picture-in-picture"
        />
      )}

      {poster ? (
        <AssetImage
          src={poster}
          alt={title || "Patient story"}
          aria-hidden
          fill
          sizes="272px"
          className="object-cover"
        />
      ) : id && near ? (
        /* eslint-disable-next-line @next/next/no-img-element */
        <img
          src={youtubeShortThumbnail(id)}
          width={1080}
          height={1920}
          alt={title || "Patient story"}
          aria-hidden
          loading="lazy"
          className={`pointer-events-none transition-opacity duration-300 ${fit} ${
            posterHidden ? "opacity-0" : "opacity-100"
          }`}
        />
      ) : null}

      {id ? (
        <button
          ref={buttonRef}
          type="button"
          onClick={() => onOpen(id)}
          onKeyDown={onKeyDown}
          tabIndex={isActive ? 0 : -1}
          aria-label={title ? `Play ${title}` : "Play patient story"}
          className="video-poster-btn group absolute inset-0 size-full cursor-pointer"
        >
          <PlayGlyph />
        </button>
      ) : (
        <span className="pointer-events-none absolute inset-0">
          <PlayGlyph />
        </span>
      )}
    </div>
  );
}

function PlayGlyph() {
  return (
    <AssetImage
      src="/assets/optilase/icons/play.svg"
      alt="Play"
      aria-hidden
      width={60}
      height={60}
      className="absolute top-1/2 left-1/2 size-15 -translate-x-1/2 -translate-y-1/2 transition-transform group-hover:scale-105"
    />
  );
}

function VideoDialog({
  videoId,
  poster,
  onClose,
}: {
  videoId: string;
  poster?: string;
  onClose: () => void;
}) {
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <>
        <DialogPrimitive.Root open onOpenChange={(v) => !v && onClose()}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="bg-navy-03/60 fixed inset-0 z-60 md:backdrop-blur-[2px]" />
            <DialogPrimitive.Content
              aria-describedby={undefined}
              tabIndex={-1}
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                (e.currentTarget as HTMLElement).focus();
              }}
              onCloseAutoFocus={(e) => e.preventDefault()}
              className="fixed top-1/2 left-1/2 z-70 flex aspect-9/16 h-[min(88vh,820px)] -translate-x-1/2 -translate-y-1/2 items-center justify-center overflow-hidden rounded-3xl bg-white p-3 shadow-[0_40px_80px_-24px_rgba(0,0,0,0.35)] outline-none"
              style={{ animation: "video-dialog-in 0.2s ease-out" }}
            >
              <DialogPrimitive.Title className="sr-only">Patient story</DialogPrimitive.Title>
              <DialogPrimitive.Close
                aria-label="Close"
                className="bg-teal-07 hover:bg-teal-08 absolute top-4 right-4 z-20 flex size-9 cursor-pointer items-center justify-center rounded-full text-white shadow-md transition-colors"
              >
                <X className="size-4" strokeWidth={2.5} />
              </DialogPrimitive.Close>
              <div className="bg-navy-03 relative aspect-9/16 w-full overflow-hidden rounded-2xl">
                <div className="absolute inset-0 size-full">
                  <YouTubePlayer
                    videoId={videoId}
                    posterSrc={poster}
                    title="Patient story"
                    inline
                    autoStart
                    autoFocus
                    ratio="9:16"
                  />
                </div>
              </div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
        <style jsx global>{`
          @keyframes video-dialog-in {
            from {
              opacity: 0;
              transform: scale(0.96) translateY(8px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
        `}</style>
      </>
    );
  }

  return (
    <DrawerPrimitive.Root open onOpenChange={(v) => !v && onClose()}>
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay className="bg-navy-03/60 fixed inset-0 z-50 md:backdrop-blur-[2px]" />
        <DrawerPrimitive.Content className="text-navy-03 fixed inset-x-0 bottom-0 z-50 mx-auto flex h-[90dvh] max-w-md flex-col rounded-t-[24px] bg-white shadow-[0_-20px_40px_-12px_rgba(0,0,0,0.25)] outline-none">
          <DrawerPrimitive.Title className="sr-only">Patient story</DrawerPrimitive.Title>
          <DrawerPrimitive.Description className="sr-only">
            Watch the full patient story video.
          </DrawerPrimitive.Description>
          <div className="bg-navy-03/15 mx-auto mt-3 h-1.5 w-10 shrink-0 rounded-full" />
          <div className="flex min-h-0 flex-1 flex-col p-3 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
            <div className="bg-navy-03 relative mx-auto aspect-9/16 h-full max-w-full overflow-hidden rounded-2xl">
              <div className="absolute inset-0 size-full">
                <YouTubePlayer
                  videoId={videoId}
                  posterSrc={poster}
                  title="Patient story"
                  inline={false}
                  autoStart
                  autoFocus
                  ratio="9:16"
                />
              </div>
            </div>
          </div>
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}

export default function TestimonialsView({
  heading,
  videos,
  ctaLabel,
  ctaHref,
  surface,
}: TestimonialsViewProps) {
  const scroller = useRef<HTMLUListElement>(null);
  const canPage = useHorizontalOverflow(scroller);
  const cardRefs = useRef<(HTMLLIElement | null)[]>([]);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const openerIndex = useRef<number | null>(null);

  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [page, setPage] = useState(0);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [hasEnteredView, setHasEnteredView] = useState(false);
  const [openVideoId, setOpenVideoId] = useState<string | null>(null);

  const { ref: arrowsRef, rovingProps } = useRovingFocus<HTMLDivElement>();

  const sync = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
    setPage(activeSlide(el));
  }, []);

  useEffect(() => {
    sync();
  }, [sync]);

  useEffect(() => {
    if (hasEnteredView) return;
    const el = scroller.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setHasEnteredView(true);
        obs.disconnect();
      },
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasEnteredView]);

  const previewingId = hasEnteredView
    ? (hoveredId ?? videos[page]?.id ?? videos[0]?.id ?? null)
    : null;

  const scrollByCard = (dir: 1 | -1) => {
    const el = scroller.current;
    if (el) scrollToAdjacent(el, dir);
  };

  const handleCardKey = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const total = buttonRefs.current.length;
    if (total === 0) return;
    let next = index;
    if (e.key === "ArrowRight") next = (index + 1) % total;
    else if (e.key === "ArrowLeft") next = (index - 1 + total) % total;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = total - 1;
    else return;
    e.preventDefault();
    buttonRefs.current[next]?.focus({ preventScroll: true });
    cardRefs.current[next]?.scrollIntoView({
      behavior: "smooth",
      inline: "nearest",
      block: "nearest",
    });
  };

  if (videos.length === 0) return null;

  return (
    <section
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn("px-5 py-10 md:px-10 md:py-12 lg:px-20 lg:py-20", surface ? "" : "bg-white")}
    >
      <div className="mx-auto max-w-7xl">
        <div className="flex items-start justify-between gap-6">
          {heading ? (
            <h2 className={cn("text-section", accentOn(surface, "text-teal-07"))}>{heading}</h2>
          ) : (
            <span />
          )}

          {canPage && (
            <div ref={arrowsRef} {...rovingProps} className="hidden shrink-0 gap-3 lg:flex">
              <button
                type="button"
                data-roving
                onClick={() => scrollByCard(-1)}
                disabled={atStart}
                aria-label="Previous testimonials"
                className={cn(
                  "flex size-12 cursor-pointer items-center justify-center rounded-full border transition-opacity disabled:opacity-40",
                  surface
                    ? cn(accentOn(surface, "text-teal-07"), "border-current/40")
                    : "border-teal-03 text-teal-07",
                )}
              >
                <Chevron dir="left" />
              </button>
              <button
                type="button"
                data-roving
                onClick={() => scrollByCard(1)}
                disabled={atEnd}
                aria-label="Next testimonials"
                className={cn(
                  "flex size-12 cursor-pointer items-center justify-center rounded-full border transition-opacity disabled:opacity-40",
                  surface
                    ? cn(accentOn(surface, "text-teal-07"), "border-current/40")
                    : "border-teal-03 text-teal-07",
                )}
              >
                <Chevron dir="right" />
              </button>
            </div>
          )}
        </div>

        <div className="relative mt-10">
          <ul
            ref={scroller}
            onScroll={sync}
            className="-mx-5 flex snap-x snap-mandatory scroll-pl-5 [scrollbar-width:none] gap-4 overflow-x-auto scroll-smooth px-5 pb-2 md:-mx-10 md:scroll-pl-10 md:px-10 lg:mx-0 lg:scroll-pl-0 lg:gap-5 lg:px-0 [&::-webkit-scrollbar]:hidden"
          >
            {videos.map((v, i) => (
              <li
                key={v.id ?? `${v.poster}-${i}`}
                ref={(el) => {
                  cardRefs.current[i] = el;
                }}
                className="snap-start"
              >
                <StoryCard
                  video={v}
                  isPreviewing={previewingId === v.id}
                  onPreviewIntent={() => v.id && setHoveredId(v.id)}
                  onOpen={(id) => {
                    track(ANALYTICS_EVENTS.VIDEO_PLAYED, {
                      module: "Patient Stories",
                      video: v.title || id,
                    });
                    openerIndex.current = i;
                    (document.activeElement as HTMLElement | null)?.blur();
                    setOpenVideoId(id);
                  }}
                  buttonRef={(el) => {
                    buttonRefs.current[i] = el;
                  }}
                  onKeyDown={(e) => handleCardKey(e, i)}
                  isActive={i === page}
                />
              </li>
            ))}
          </ul>
        </div>

        <CarouselDots
          count={videos.length}
          active={page}
          label="testimonial"
          onSelect={(i) =>
            cardRefs.current[i]?.scrollIntoView({
              behavior: "smooth",
              inline: "start",
              block: "nearest",
            })
          }
          className="mt-2 lg:hidden"
        />

        {ctaLabel && ctaHref ? (
          <div className="mt-8 flex justify-center lg:mt-10">
            <ButtonLink href={ctaHref} variant="teal">
              {ctaLabel}
            </ButtonLink>
          </div>
        ) : null}
      </div>

      {openVideoId && (
        <VideoDialog
          videoId={openVideoId}
          poster={videos.find((v) => v.id === openVideoId)?.poster}
          onClose={() => {
            setOpenVideoId(null);
            const i = openerIndex.current;
            if (i != null) requestAnimationFrame(() => buttonRefs.current[i]?.focus());
          }}
        />
      )}
    </section>
  );
}
